import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { SocketProvider } from './contexts/SocketContext';
import { InvitationProvider, InvitationNavigationHandler } from './contexts/InvitationContext';
import PersistentInvitationBanner from './components/PersistentInvitationBanner';
import GlobalChat from './components/GlobalChat';
import PushNotifications from './components/PushNotifications';
import Login from './pages/Login';
import MainMenu from './pages/MainMenu';
import Game from './pages/Game';
import Statistics from './pages/Statistics';
import Settings from './pages/Settings';
import MultiplayerLobby from './pages/MultiplayerLobby';
import GameLobby from './pages/GameLobby';
import Friends from './pages/Friends';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    // Check authentication status
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_URL}/auth/me`, {
          credentials: 'include'
        });
        const data = await response.json();
        setIsAuthenticated(data.authenticated || false);
      } catch (err) {
        setIsAuthenticated(false);
      }
    };

    checkAuth();

    // Listen for storage events to update auth status (e.g., after login)
    const handleStorageChange = () => {
      checkAuth();
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically (every 5 seconds) to catch login/logout
    const interval = setInterval(checkAuth, 5000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Show loading while checking auth
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
        <div className="text-white text-xl">Lädt...</div>
      </div>
    );
  }

  return (
    <SocketProvider isAuthenticated={isAuthenticated}>
      {isAuthenticated && (
        <InvitationProvider>
          <Router>
            <PersistentInvitationBanner />
            <GlobalChat />
            <PushNotifications />
            <InvitationNavigationHandler />
            <Routes>
              <Route path="/login" element={<Navigate to="/menu" replace />} />
              <Route path="/menu" element={<MainMenu onLogout={() => setIsAuthenticated(false)} />} />
              <Route path="/game" element={<Game />} />
              <Route path="/multiplayer" element={<MultiplayerLobby />} />
              <Route path="/lobby" element={<GameLobby />} />
              <Route path="/friends" element={<Friends />} />
              <Route path="/stats" element={<Statistics />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/" element={<Navigate to="/menu" replace />} />
            </Routes>
          </Router>
        </InvitationProvider>
      )}
      {!isAuthenticated && (
        <Router>
          <Routes>
            <Route path="/login" element={<Login onLogin={() => setIsAuthenticated(true)} />} />
            <Route path="/" element={<Navigate to="/login" replace />} />
          </Routes>
        </Router>
      )}
    </SocketProvider>
  );
}

export default App;

